import { Button, Drawer, Input, Modal, Select, Space, Table } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { getSetting } from "../../../redux/actions/API/settingAction";
import { isValidJSON } from "../../utility/itemsUtility";
import { QRCode } from "antd";

interface BarcodeItem {
    itemId: string;
    itemName: string;
    quantity: number;
    uom: string;
    prefix: string;
    sequence: string;
}

interface BarcodePrintProps {
    data: BarcodeItem[];
    isVisible: boolean;
    setIsVisible: Function;
    companyId: string;
    getSetting: Function;
    settingData: any;
    type?: "itemIn" | "itemOut" | string;
    companyName?: string;
}

const BarcodePrint: React.FC<BarcodePrintProps> = ({
    data = [],
    isVisible,
    setIsVisible,
    companyId,
    getSetting,
    settingData,
    type = "itemIn",
    companyName
}) => {
    const printRef = useRef<HTMLDivElement>(null);

    const [itemInBarcodeSettings, setItemInBarcodeSettings] = useState<any>({});
    const [itemOutBarcodeSettings, setItemOutBarcodeSettings] = useState<any>({});

    const [customWidth, setCustomWidth] = useState<string | number>("");
    const [customHeight, setCustomHeight] = useState<string | number>("");
    const [customUnit, setCustomUnit] = useState<"mm" | "inch">("mm");

    const [printOpen, setPrintOpen] = useState(false);
    const [printData, setPrintData] = useState<any[]>([]);
    const [localData, setLocalData] = useState<any[]>([]);

    // ---------------------------------------------------------
    // Load settings
    // ---------------------------------------------------------

    useEffect(() => {
        if (companyId) {
            getSetting({ companyId });
        }
    }, [companyId, getSetting]);

    // ---------------------------------------------------------
    // Set barcode settings
    // ---------------------------------------------------------

    useEffect(() => {
        const itemIn =
            isValidJSON(settingData?.itemInBarcodeSettings) || {};

        const itemOut =
            isValidJSON(settingData?.itemOutBarcodeSettings) || {};

        setItemInBarcodeSettings(itemIn);
        setItemOutBarcodeSettings(itemOut);

        const active = type === "itemOut" ? itemOut : itemIn;

        if (active?.unit) {
            setCustomUnit(active.unit);
        }

        if (active?.width !== undefined && active?.width !== null) {
            setCustomWidth(active.width);
        }

        if (active?.height !== undefined && active?.height !== null) {
            setCustomHeight(active.height);
        }
    }, [settingData, type]);

    // ---------------------------------------------------------
    // Set local data
    // ---------------------------------------------------------

    useEffect(() => {
        if (Array.isArray(data)) {
            setLocalData([...data]);
        }
    }, [data]);

    // ---------------------------------------------------------
    // Active settings
    // ---------------------------------------------------------

    const activeSettings =
        type === "itemOut"
            ? itemOutBarcodeSettings
            : itemInBarcodeSettings;

    // ---------------------------------------------------------
    // Dimensions
    // ---------------------------------------------------------

    const widthVal = Number(
        customWidth !== ""
            ? customWidth
            : activeSettings?.width || 0
    );

    const heightVal = Number(
        customHeight !== ""
            ? customHeight
            : activeSettings?.height || 0
    );

    // Convert to mm
    const widthMm =
        customUnit === "inch"
            ? widthVal * 25.4
            : widthVal;

    const heightMm =
        customUnit === "inch"
            ? heightVal * 25.4
            : heightVal;

    // ---------------------------------------------------------
    // Scaling
    // ---------------------------------------------------------

    const scale =
        heightMm > 0 && widthMm > 0
            ? Math.min(
                  1.3,
                  Math.max(
                      0.7,
                      Math.min(widthMm / 70, heightMm / 50)
                  )
              )
            : 1;

    const titleFontSize = Math.round(14 * scale);
    const subTitleFontSize = Math.round(12.5 * scale);
    const textFontSize = Math.round(11 * scale);
    const barcodeFontSize = Math.round(12.5 * scale);

    // ---------------------------------------------------------
    // QR Code size
    // ---------------------------------------------------------

    const activeColumns = activeSettings?.columns || [];

    const textLinesCount =
        activeColumns.length || 3;

    const textHeightEstimateMm =
        textLinesCount *
        (textFontSize * 0.35 + 2);

    const availableHeightForQr =
        heightMm > 0
            ? Math.max(
                  15,
                  heightMm -
                      textHeightEstimateMm -
                      14
              )
            : 35;

    const qrDimensionMm =
        widthMm > 0 && heightMm > 0
            ? Math.max(
                  15,
                  Math.min(
                      widthMm * 0.7,
                      availableHeightForQr,
                      45
                  )
              )
            : widthMm > 0
            ? Math.min(widthMm * 0.7, 60)
            : 35;

    const qrSize = Math.round(
        qrDimensionMm * 3.78
    );

    const cardWidthCss =
        widthMm > 0
            ? `${widthMm}mm`
            : "220px";

    const cardHeightCss =
        heightMm > 0
            ? `${heightMm}mm`
            : "auto";

    // ---------------------------------------------------------
    // PRINT
    // ---------------------------------------------------------

    const handlePrint = () => {
        if (!printRef.current) {
            return;
        }

        // -----------------------------------------------------
        // Validate dimensions
        // -----------------------------------------------------

        if (
            !widthMm ||
            !heightMm ||
            widthMm <= 0 ||
            heightMm <= 0
        ) {
            console.error("Invalid barcode dimensions", {
                widthMm,
                heightMm,
                widthVal,
                heightVal,
                customUnit,
                activeSettings
            });

            alert(
                "Please enter a valid label width and height."
            );

            return;
        }

        // -----------------------------------------------------
        // Clone preview
        // -----------------------------------------------------

        const clone =
            printRef.current.cloneNode(
                true
            ) as HTMLElement;

        // -----------------------------------------------------
        // Convert QR canvases to images
        // -----------------------------------------------------

        const originalCanvases =
            printRef.current.querySelectorAll(
                "canvas"
            );

        const clonedCanvases =
            clone.querySelectorAll("canvas");

        clonedCanvases.forEach(
            (canvas, index) => {
                const originalCanvas =
                    originalCanvases[index];

                if (!originalCanvas) {
                    return;
                }

                try {
                    const img =
                        document.createElement(
                            "img"
                        );

                    img.src =
                        originalCanvas.toDataURL(
                            "image/png"
                        );

                    img.style.width =
                        `${qrDimensionMm}mm`;

                    img.style.height =
                        `${qrDimensionMm}mm`;

                    img.style.maxWidth =
                        "100%";

                    img.style.maxHeight =
                        "100%";

                    img.style.objectFit =
                        "contain";

                    img.style.display =
                        "block";

                    img.style.margin =
                        "2px auto";

                    canvas.replaceWith(img);
                } catch (error) {
                    console.error(
                        "Unable to convert QR canvas",
                        error
                    );
                }
            }
        );

        // -----------------------------------------------------
        // Remove preview styles
        //
        // IMPORTANT:
        // Do not set breakAfter here.
        // Pagination is handled entirely by CSS.
        // -----------------------------------------------------

        const clonedCards =
            clone.querySelectorAll(
                ".barcode-card"
            );

        clonedCards.forEach((card) => {
            const cardEl =
                card as HTMLElement;

            cardEl.style.border =
                "none";

            cardEl.style.borderRadius =
                "0";

            cardEl.style.boxShadow =
                "none";

            cardEl.style.margin =
                "0";

            cardEl.style.breakAfter =
                "auto";

            cardEl.style.pageBreakAfter =
                "auto";
        });

        const printContents =
            clone.innerHTML;

        // -----------------------------------------------------
        // Open print window
        // -----------------------------------------------------

        const newWindow =
            window.open(
                "",
                "_blank",
                "width=900,height=650"
            );

        if (!newWindow) {
            alert(
                "Please allow popups for this website to print."
            );

            return;
        }

        // -----------------------------------------------------
        // Print HTML
        // -----------------------------------------------------

        newWindow.document.write(`
<!DOCTYPE html>
<html>
<head>

    <meta charset="utf-8" />

    <title>Print Barcode</title>

    <style>

        /*
         * IMPORTANT:
         * Use SINGLE braces here.
         * This is already a JavaScript template literal.
         */

        @page {
            size: ${widthMm}mm ${heightMm}mm;
            margin: 0;
        }

        * {
            box-sizing: border-box;
        }

        html {
            margin: 0 !important;
            padding: 0 !important;

            width: ${widthMm}mm !important;
        }

        body {
            margin: 0 !important;
            padding: 0 !important;

            width: ${widthMm}mm !important;

            font-family:
                Arial,
                Helvetica,
                sans-serif;

            background: #ffffff !important;

            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }

        /*
         * Container
         */

        .barcode-grid {
            display: block !important;

            width: ${widthMm}mm !important;

            margin: 0 !important;
            padding: 0 !important;
        }

        /*
         * Each barcode is EXACTLY one page.
         */

        .barcode-card {

            width: ${widthMm}mm !important;

            height: ${heightMm}mm !important;

            min-width: ${widthMm}mm !important;
            max-width: ${widthMm}mm !important;

            min-height: ${heightMm}mm !important;
            max-height: ${heightMm}mm !important;

            margin: 0 !important;

            padding: 1mm 0 !important;

            display: flex !important;

            flex-direction: column !important;

            align-items: center !important;

            justify-content: center !important;

            text-align: center !important;

            background: #ffffff !important;

            border: none !important;

            border-radius: 0 !important;

            box-shadow: none !important;

            overflow: hidden !important;

            box-sizing: border-box !important;

            break-inside: avoid !important;

            page-break-inside: avoid !important;

            /*
             * VERY IMPORTANT:
             * Never force a break AFTER a label.
             */

            break-after: auto !important;

            page-break-after: auto !important;
        }

        /*
         * Start every label after the first
         * on a NEW page.
         */

        .barcode-card:not(:first-child) {

            break-before: page !important;

            page-break-before: always !important;
        }

        /*
         * QR image
         */

        .barcode-card img {

            width: ${qrDimensionMm}mm !important;

            height: ${qrDimensionMm}mm !important;

            max-width:
                calc(100% - 2mm) !important;

            max-height:
                calc(100% - 2mm) !important;

            object-fit: contain !important;

            display: block !important;

            margin: 2px auto !important;
        }

        /*
         * Text
         */

        .barcode-card p {

            width: 100% !important;

            max-width: 100% !important;

            margin: 1px 0 !important;

            padding: 0 !important;

            line-height: 1.2 !important;

            word-break: break-word !important;

            overflow-wrap: anywhere !important;
        }

        /*
         * Print
         */

        @media print {

            html {

                width: ${widthMm}mm !important;

                margin: 0 !important;

                padding: 0 !important;
            }

            body {

                width: ${widthMm}mm !important;

                margin: 0 !important;

                padding: 0 !important;
            }

            .barcode-grid {

                display: block !important;

                width: ${widthMm}mm !important;

                margin: 0 !important;

                padding: 0 !important;
            }

            .barcode-card {

                width: ${widthMm}mm !important;

                height: ${heightMm}mm !important;

                min-width: ${widthMm}mm !important;

                max-width: ${widthMm}mm !important;

                min-height: ${heightMm}mm !important;

                max-height: ${heightMm}mm !important;

                margin: 0 !important;

                padding: 1mm 0 !important;

                overflow: hidden !important;

                break-inside: avoid !important;

                page-break-inside: avoid !important;

                break-after: auto !important;

                page-break-after: auto !important;
            }

            /*
             * Every label after first starts
             * on a new printer page.
             */

            .barcode-card:not(:first-child) {

                break-before: page !important;

                page-break-before: always !important;
            }

            .barcode-card img {

                width: ${qrDimensionMm}mm !important;

                height: ${qrDimensionMm}mm !important;

                max-width:
                    calc(100% - 2mm) !important;

                max-height:
                    calc(100% - 2mm) !important;

                object-fit: contain !important;

                display: block !important;
            }
        }

    </style>

</head>

<body>

    <div class="barcode-grid">

        ${printContents}

    </div>

    <script>

        window.onload = function () {

            /*
             * Give the browser time to load
             * converted QR images.
             */

            setTimeout(function () {

                window.print();

            }, 700);

        };

        window.onafterprint = function () {

            setTimeout(function () {

                window.close();

            }, 100);

        };

    </script>

</body>

</html>
        `);

        newWindow.document.close();
    };

    // ---------------------------------------------------------
    // Table columns
    // ---------------------------------------------------------

    const columns: any = [
        {
            title: "Item Id",
            dataIndex: "itemId",
            key: "itemId"
        },
        {
            title: "Item Name",
            dataIndex: "itemName",
            key: "itemName"
        },
        {
            title: "Barcode Number",
            dataIndex: "barcode",
            key: "barcode",

            render: (
                text: any,
                item: any
            ) =>
                item?.BarcodeNumber
                    ? item.BarcodeNumber
                    : `${item.prefix || ""}${item.sequence || ""}`
        },
        {
            title: "Number Of Copies",
            dataIndex: "copies",
            key: "copies",

            render: (
                text: any,
                item: any,
                index: number
            ) => {
                return (
                    <Input
                        type="number"
                        min={1}
                        value={
                            item.copies || 1
                        }
                        onChange={(e) => {
                            const arr = [
                                ...localData
                            ];

                            arr[index] = {
                                ...arr[index],

                                copies:
                                    Number(
                                        e.target
                                            .value
                                    ) || 1
                            };

                            setLocalData(
                                arr
                            );
                        }}
                    />
                );
            }
        }
    ];

    // ---------------------------------------------------------
    // Render
    // ---------------------------------------------------------

    return (
        <>
            {/* =================================================
                COPY SELECTION DRAWER
            ================================================= */}

            <Drawer
                title="Select Number of Copies"
                width={800}
                onClose={() =>
                    setIsVisible(false)
                }
                open={isVisible}
                extra={
                    <Space>

                        <Button
                            onClick={() =>
                                setIsVisible(
                                    false
                                )
                            }
                        >
                            Cancel
                        </Button>

                        <Button
                            type="primary"
                            onClick={() => {

                                const temp: any[] =
                                    [];

                                for (
                                    const element of
                                        [
                                            ...localData
                                        ]
                                ) {

                                    const copies =
                                        Number(
                                            element.copies ||
                                                1
                                        );

                                    for (
                                        let i = 0;
                                        i < copies;
                                        i++
                                    ) {

                                        temp.push(
                                            {
                                                ...element
                                            }
                                        );
                                    }
                                }

                                setPrintData(
                                    temp
                                );

                                setPrintOpen(
                                    true
                                );
                            }}
                        >
                            Print
                        </Button>

                    </Space>
                }
            >
                <Table
                    dataSource={
                        localData
                    }
                    columns={
                        columns
                    }
                    pagination={
                        false
                    }
                    rowKey={(
                        record,
                        index
                    ) =>
                        `${record.itemId}-${index}`
                    }
                />
            </Drawer>

            {/* =================================================
                PRINT PREVIEW
            ================================================= */}

            <Modal
                closable={false}
                title={
                    <div
                        style={{
                            display: "flex",
                            justifyContent:
                                "space-between",
                            alignItems:
                                "center",
                            flexWrap:
                                "wrap",
                            gap: "8px"
                        }}
                    >

                        <span
                            style={{
                                fontWeight: 600,
                                fontSize:
                                    "16px"
                            }}
                        >
                            Preview
                        </span>

                        <div
                            style={{
                                display: "flex",
                                alignItems:
                                    "center",
                                gap: "10px",
                                flexWrap:
                                    "wrap"
                            }}
                        >

                            <Space
                                size="small"
                            >

                                <span
                                    style={{
                                        fontSize:
                                            "12px",
                                        color:
                                            "#666"
                                    }}
                                >
                                    Unit:
                                </span>

                                <Select
                                    value={
                                        customUnit
                                    }
                                    onChange={(
                                        val
                                    ) =>
                                        setCustomUnit(
                                            val
                                        )
                                    }
                                    style={{
                                        width: 85
                                    }}
                                    options={[
                                        {
                                            label:
                                                "mm",
                                            value:
                                                "mm"
                                        },
                                        {
                                            label:
                                                "inch",
                                            value:
                                                "inch"
                                        }
                                    ]}
                                />

                                <span
                                    style={{
                                        fontSize:
                                            "12px",
                                        color:
                                            "#666"
                                    }}
                                >
                                    Width:
                                </span>

                                <Input
                                    type="number"
                                    min={0}
                                    step={
                                        customUnit ===
                                        "inch"
                                            ? "0.1"
                                            : "1"
                                    }
                                    placeholder={
                                        customUnit
                                    }
                                    style={{
                                        width: 90
                                    }}
                                    value={
                                        customWidth
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setCustomWidth(
                                            e
                                                .target
                                                .value
                                        )
                                    }
                                    suffix={
                                        customUnit
                                    }
                                />

                                <span
                                    style={{
                                        fontSize:
                                            "12px",
                                        color:
                                            "#666"
                                    }}
                                >
                                    Height:
                                </span>

                                <Input
                                    type="number"
                                    min={0}
                                    step={
                                        customUnit ===
                                        "inch"
                                            ? "0.1"
                                            : "1"
                                    }
                                    placeholder={
                                        customUnit
                                    }
                                    style={{
                                        width: 90
                                    }}
                                    value={
                                        customHeight
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setCustomHeight(
                                            e
                                                .target
                                                .value
                                        )
                                    }
                                    suffix={
                                        customUnit
                                    }
                                />

                            </Space>

                            <Button
                                onClick={
                                    handlePrint
                                }
                                type="primary"
                            >
                                Print
                            </Button>

                            <Button
                                onClick={() =>
                                    setPrintOpen(
                                        false
                                    )
                                }
                                type="link"
                            >
                                Cancel
                            </Button>

                        </div>
                    </div>
                }
                open={printOpen}
                footer={null}
                width={920}
                onCancel={() =>
                    setPrintOpen(false)
                }
            >

                {/* =================================================
                    PREVIEW AREA
                ================================================= */}

                <div
                    ref={printRef}
                    style={{
                        display: "flex",
                        flexWrap:
                            "wrap",
                        gap: "15px",
                        maxHeight:
                            "500px",
                        overflow:
                            "auto",
                        justifyContent:
                            "center",
                        alignItems:
                            "flex-start",
                        padding: "12px",
                        background:
                            "#fafafa",
                        borderRadius:
                            "6px"
                    }}
                >

                    {printData?.map(
                        (
                            item: any,
                            index: number
                        ) => (

                            <div
                                key={index}
                                className="barcode-card"

                                style={{
                                    width:
                                        cardWidthCss,

                                    minWidth:
                                        cardWidthCss,

                                    maxWidth:
                                        cardWidthCss,

                                    height:
                                        heightMm >
                                        0
                                            ? cardHeightCss
                                            : "auto",

                                    minHeight:
                                        heightMm >
                                        0
                                            ? cardHeightCss
                                            : "auto",

                                    maxHeight:
                                        heightMm >
                                        0
                                            ? cardHeightCss
                                            : "none",

                                    boxSizing:
                                        "border-box",

                                    display:
                                        "flex",

                                    flexDirection:
                                        "column",

                                    alignItems:
                                        "center",

                                    justifyContent:
                                        "center",

                                    textAlign:
                                        "center",

                                    border:
                                        "1px dashed #bbb",

                                    borderRadius:
                                        "6px",

                                    padding:
                                        "6px 8px",

                                    background:
                                        "#fff",

                                    overflow:
                                        "hidden"
                                }}
                            >

                                {/* Company */}

                                {activeSettings?.columns?.includes(
                                    "companyName"
                                ) &&
                                    (
                                        item?.companyName ||
                                        companyName
                                    ) && (

                                        <p
                                            style={{
                                                fontWeight:
                                                    700,
                                                fontSize:
                                                    `${titleFontSize}px`,
                                                margin:
                                                    "1px 0",
                                                lineHeight:
                                                    1.2,
                                                wordBreak:
                                                    "break-word",
                                                maxWidth:
                                                    "100%",
                                                color:
                                                    "#000"
                                            }}
                                        >
                                            {
                                                item?.companyName ||
                                                companyName
                                            }
                                        </p>
                                    )}

                                {/* Item ID */}

                                {activeSettings?.columns?.includes(
                                    "itemId"
                                ) &&
                                    item?.itemId && (

                                        <p
                                            style={{
                                                fontWeight:
                                                    600,
                                                fontSize:
                                                    `${subTitleFontSize}px`,
                                                margin:
                                                    "1px 0",
                                                lineHeight:
                                                    1.2,
                                                wordBreak:
                                                    "break-word",
                                                maxWidth:
                                                    "100%",
                                                color:
                                                    "#111"
                                            }}
                                        >
                                            {
                                                item.itemId
                                            }
                                        </p>
                                    )}

                                {/* Item Name */}

                                {activeSettings?.columns?.includes(
                                    "itemName"
                                ) &&
                                    item?.itemName && (

                                        <p
                                            style={{
                                                fontSize:
                                                    `${textFontSize}px`,
                                                fontWeight:
                                                    500,
                                                margin:
                                                    "1px 0",
                                                lineHeight:
                                                    1.2,
                                                wordBreak:
                                                    "break-word",
                                                maxWidth:
                                                    "100%",
                                                color:
                                                    "#222"
                                            }}
                                        >
                                            {
                                                item.itemName
                                            }
                                        </p>
                                    )}

                                {/* Quantity */}

                                {activeSettings?.columns?.includes(
                                    "quantity"
                                ) &&
                                    item?.quantity !==
                                        undefined && (

                                        <p
                                            style={{
                                                fontSize:
                                                    `${textFontSize}px`,
                                                fontWeight:
                                                    600,
                                                margin:
                                                    "1px 0",
                                                lineHeight:
                                                    1.2,
                                                color:
                                                    "#222"
                                            }}
                                        >
                                            {
                                                item.quantity
                                            }{" "}
                                            {
                                                item.uom ||
                                                ""
                                            }
                                        </p>
                                    )}

                                {/* Manufacturing Date */}

                                {activeSettings?.columns?.includes(
                                    "manufacturingDate"
                                ) &&
                                    item?.manufacturingDate && (

                                        <p
                                            style={{
                                                fontSize:
                                                    `${Math.max(
                                                        10,
                                                        textFontSize -
                                                            1
                                                    )}px`,
                                                fontWeight:
                                                    500,
                                                margin:
                                                    "1px 0",
                                                lineHeight:
                                                    1.2,
                                                color:
                                                    "#333"
                                            }}
                                        >
                                            Mfg:{" "}
                                            {
                                                item.manufacturingDate
                                            }
                                        </p>
                                    )}

                                {/* Expiry Date */}

                                {activeSettings?.columns?.includes(
                                    "expiryDate"
                                ) &&
                                    item?.expiryDate && (

                                        <p
                                            style={{
                                                fontSize:
                                                    `${Math.max(
                                                        10,
                                                        textFontSize -
                                                            1
                                                    )}px`,
                                                fontWeight:
                                                    500,
                                                margin:
                                                    "1px 0",
                                                lineHeight:
                                                    1.2,
                                                color:
                                                    "#333"
                                            }}
                                        >
                                            Exp:{" "}
                                            {
                                                item.expiryDate
                                            }
                                        </p>
                                    )}

                                {/* Document Number */}

                                {activeSettings?.columns?.includes(
                                    "documentNumber"
                                ) &&
                                    item?.documentNumber && (

                                        <p
                                            style={{
                                                fontSize:
                                                    `${Math.max(
                                                        10,
                                                        textFontSize -
                                                            1
                                                    )}px`,
                                                fontWeight:
                                                    500,
                                                margin:
                                                    "1px 0",
                                                lineHeight:
                                                    1.2,
                                                wordBreak:
                                                    "break-word",
                                                maxWidth:
                                                    "100%",
                                                color:
                                                    "#333"
                                            }}
                                        >
                                            {
                                                item.documentNumber
                                            }
                                        </p>
                                    )}

                                {/* QR Code */}

                                <div
                                    style={{
                                        display:
                                            "flex",

                                        justifyContent:
                                            "center",

                                        alignItems:
                                            "center",

                                        marginTop:
                                            "4px",

                                        marginBottom:
                                            "4px",

                                        width:
                                            `${qrDimensionMm}mm`,

                                        height:
                                            `${qrDimensionMm}mm`,

                                        maxWidth:
                                            "100%",

                                        maxHeight:
                                            "100%"
                                    }}
                                >

                                    <QRCode
                                        size={
                                            qrSize
                                        }
                                        errorLevel="H"
                                        value={JSON.stringify(
                                            item
                                        )}
                                    />

                                </div>

                                {/* Barcode Number */}

                                {activeSettings?.columns?.includes(
                                    "barcodeNumber"
                                ) && (

                                    <p
                                        style={{
                                            fontSize:
                                                `${barcodeFontSize}px`,
                                            fontWeight:
                                                700,
                                            letterSpacing:
                                                "0.5px",
                                            margin:
                                                "2px 0",
                                            lineHeight:
                                                1.2,
                                            wordBreak:
                                                "break-all",
                                            maxWidth:
                                                "100%",
                                            color:
                                                "#000"
                                        }}
                                    >
                                        {item?.BarcodeNumber
                                            ? item.BarcodeNumber
                                            : `${item.prefix || ""}${item.sequence || ""}`}
                                    </p>

                                )}

                            </div>

                        )
                    )}

                </div>

            </Modal>
        </>
    );
};

// =============================================================
// Redux
// =============================================================

const mapStateToProps = (
    state: any
) => ({
    settingData:
        state.api.getSetting?.data,

    companyId:
        state.api.login?.data?.companyId,

    companyName:
        state.api.login?.data?.companyName
});

const mapDispatchToProps = (
    dispatch: any
) => ({
    getSetting: (
        payload: any
    ) =>
        dispatch(
            getSetting(payload)
        )
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(BarcodePrint);